import { Inject, Injectable, InjectionToken } from "@angular/core";
import { ResolveEnd, Router } from "@angular/router";

import { BehaviorSubject, Observable } from "rxjs";
import { filter } from "rxjs/operators";

import * as _ from "lodash";

// Injection token for the core custom settings
export const CORE_CUSTOM_CONFIG = new InjectionToken("coreCustomConfig");

@Injectable({
  providedIn: "root"
})
export class CoreConfigService {
  // Private
  public localConfig: any;
  private readonly _defaultConfig: any;
  private _configSubject: BehaviorSubject<any>;

  /**
   * Constructor
   *
   * @param _config
   * @param {Router} _router
   */
  constructor(
    private _router: Router,
    @Inject(CORE_CUSTOM_CONFIG) private _config
  ) {
    // Get the config from local storage
    if (_config.layout.enableLocalStorage) {
      try {
        this.localConfig = JSON.parse(localStorage.getItem("config"));
        // Sanitize: ensure wall mode didn't permanently hide menu/navbar in storage
        if (this.localConfig?.layout) {
          if (this.localConfig.layout.navbar?.hidden === true && !window.location.search.includes("wall=1")) {
            this.localConfig.layout.navbar.hidden = false;
          }
          if (this.localConfig.layout.menu?.hidden === true && !window.location.search.includes("wall=1")) {
            this.localConfig.layout.menu.hidden = false;
          }
          localStorage.setItem("config", JSON.stringify(this.localConfig));
        }
      } catch (_) {
        this.localConfig = null;
      }
    } else {
      localStorage.removeItem("config");
    }

    // Set the defaultConfig to localConfig if we have else appConfig (app-config.ts)
    this._defaultConfig = this.localConfig ? this.localConfig : _config;

    // Initialize the config service
    this._initConfig();
  }

  //  Accessors
  // -----------------------------------------------------------------------------------------------------

  // Set the config
  set config(data) {
    let config;

    // Set config = localConfig, If we have else defaultConfig
    if (this.localConfig) {
      config = this.localConfig;
    } else {
      config = this._configSubject.getValue();
    }

    // Merge provided data with config, and create new merged config
    config = _.merge({}, config, data);

    // Set config to local storage if enableLocalStorage parameter is true and not in wall mode
    const isWall = window.location.search.includes("wall=1");
    if (config.layout.enableLocalStorage && !isWall) {
      localStorage.setItem("config", JSON.stringify(config));
    }

    // Inform the observers
    this._configSubject.next(config);
  }

  // Get the config
  get config(): any | Observable<any> {
    return this._configSubject.asObservable();
  }

  /**
   * Get default config
   *
   * @returns {any}
   */
  get defaultConfig(): any {
    return this._defaultConfig;
  }

  // Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Initialize
   *
   * @private
   */
  private _initConfig(): void {
    // Set the config from the default config
    this._configSubject = new BehaviorSubject(_.cloneDeep(this._defaultConfig));

    // On every RoutesRecognized event
    // Check if localDefault (localStorage if we have else defaultConfig) is different form the default one
    this._router.events
      .pipe(filter((event) => event instanceof ResolveEnd))
      .subscribe((event: any) => {
        // Get the local config from local storage
        try {
          this.localConfig = JSON.parse(localStorage.getItem("config"));
        } catch (_) {
          this.localConfig = null;
        }

        // Set localDefault to localConfig if we have else defaultConfig
        let localDefault = this.localConfig
          ? this.localConfig
          : this._defaultConfig;

        const isWall = (event.urlAfterRedirects || event.url || window.location.href).includes("wall=1");
        const currentConfig = this._configSubject.getValue();
        const config = _.cloneDeep(currentConfig);

        if (isWall) {
          config.layout.navbar.hidden = true;
          config.layout.menu.hidden = true;
          config.layout.footer.hidden = true;
          this._configSubject.next(config);
        } else {
          // Reset the layout from localDefault, ensuring navbar/menu are visible
          let targetLayout = _.cloneDeep(localDefault.layout);
          targetLayout.navbar.hidden = false;
          targetLayout.menu.hidden = false;

          if (!_.isEqual(currentConfig.layout, targetLayout)) {
            config.layout = targetLayout;
            this._configSubject.next(config);
          }
        }
      });
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Set config
   *
   * @param data
   * @param {{emitEvent: boolean}} param
   * @param {boolean} saveToStorage
   */
  setConfig(data, param = { emitEvent: true }, saveToStorage = true): void {
    let config;

    // Set config = localConfig, If we have else defaultConfig
    try {
      this.localConfig = JSON.parse(localStorage.getItem("config"));
    } catch (_) {
      this.localConfig = null;
    }
    if (this.localConfig) {
      config = this.localConfig;
    } else {
      config = this._configSubject.getValue();
    }

    // Merge provided value with config, and create new merged config
    config = _.merge({}, config, data);

    // Set config to local storage only if enableLocalStorage parameter is true AND saveToStorage is true
    const isWall = window.location.search.includes("wall=1");
    if (config.layout.enableLocalStorage && saveToStorage && !isWall) {
      localStorage.setItem("config", JSON.stringify(config));
    }

    // If emitEvent option is true...
    if (param.emitEvent === true) {
      // Inform the observers
      this._configSubject.next(config);
    }
  }

  /**
   * Get config
   *
   * @returns {Observable<any>}
   */
  getConfig(): Observable<any> {
    return this._configSubject.asObservable();
  }

  /**
   * Reset to the default config
   */
  resetConfig(): void {
    this._configSubject.next(_.cloneDeep(this._defaultConfig));
  }

  setLanguageForLayout(language: string): void {
    let config = this._configSubject.getValue();

    // Chỉ thay đổi app.appLanguage
    config = {
      ...config,
      app: {
        ...config.app,
        appLanguage: language
      }
    };

    if (config.layout.enableLocalStorage) {
      localStorage.setItem("config", JSON.stringify(config));
    }

    this._configSubject.next(config);
  }
}
